<template>
  <!--
    The steps of a turn as connected dots, shared by the chat's status line and the
    drawer's Activity board. A label is the model's one line for the step.
  -->
  <ol class="activity-steps" :class="`activity-steps--${size}`">
    <li v-for="(step, index) in steps" :key="step.id" class="activity-step">
      <span class="activity-step__rail" aria-hidden="true">
        <status-spinner
          v-if="current(step, index)"
          :size="12"
          class="activity-step__spinner"
        />
        <span v-else class="activity-step__dot"></span>
        <span
          v-if="index < steps.length - 1 || done"
          class="activity-step__line"
        ></span>
      </span>
      <span
        class="activity-step__label"
        :class="{ 'activity-step__label--active': current(step, index) }"
      >
        <shimmer-text v-if="current(step, index)">{{
          step.label
        }}</shimmer-text>
        <template v-else>{{ step.label }}</template>
        <!-- The spinner says it to the eye; this says it to a screen reader. -->
        <span v-if="current(step, index)" class="sr-only">, in progress</span>
      </span>
    </li>
    <!-- One check, at the end only: without it the list read as cut off, not finished. -->
    <li v-if="done && steps.length" class="activity-step activity-step--done">
      <span class="activity-step__rail" aria-hidden="true">
        <svg
          class="activity-step__check"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
        >
          <circle cx="12" cy="12" r="11" />
          <path
            d="M7 12.5l3.2 3.2L17 9"
            stroke-width="2.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
      <span class="activity-step__label activity-step__label--done">Done</span>
    </li>
  </ol>
</template>

<script>
import ShimmerText from "@/components/OliverBotV2/shared/ShimmerText.vue";
import StatusSpinner from "@/components/OliverBotV2/shared/StatusSpinner.vue";

export default {
  name: "ActivityStepList",
  components: { ShimmerText, StatusSpinner },
  props: {
    /** Step[]: { id, status: 'active'|'done', label }. */
    steps: {
      type: Array,
      default: () => [],
    },
    /** "md" in the chat, "sm" in the drawer. */
    size: {
      type: String,
      default: "md",
    },
    /**
     * Oliver is still working: the newest step stays current until the next one starts,
     * as the chat's line does, so the list never looks stopped between steps.
     */
    working: {
      type: Boolean,
      default: false,
    },
    /** The work finished: the list closes with "Done". */
    done: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    current(step, index) {
      return (
        step.status === "active" ||
        (this.working && index === this.steps.length - 1)
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.activity-steps {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
  line-height: 1.45;
}

.activity-steps--md {
  font-size: 14px;
}

.activity-steps--sm {
  font-size: 13px;
}

.activity-step {
  display: flex;
  gap: 11px;
}

// The dot, then the line down to the next one. The column stretches with the row,
// so the line reaches the next dot however far the label wraps.
.activity-step__rail {
  width: 14px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.activity-step__dot {
  width: 8px;
  height: 8px;
  margin-top: calc(0.725em - 4px);
  border-radius: 50%;
  background: var(--oliver-accent);
  opacity: 0.75;
}

.activity-step__spinner {
  margin-top: calc(0.725em - 6px);
}

.activity-step__line {
  flex: 1;
  width: 2px;
  margin-top: 4px;
  border-radius: 1px;
  background: var(--oliver-accent);
  opacity: 0.22;
}

.activity-step__label {
  min-width: 0;
  padding-bottom: 12px;
  overflow-wrap: anywhere;
  color: var(--oliver-text);
  opacity: 0.78;
}

.activity-step:last-child .activity-step__label {
  padding-bottom: 0;
}

.activity-step__label--active {
  font-weight: 500;
  opacity: 1;
}

.activity-step__check {
  margin-top: calc(0.725em - 7px);

  circle {
    fill: var(--oliver-accent);
  }

  path {
    stroke: #ffffff;
  }
}

.activity-step__label--done {
  font-weight: 600;
  color: var(--oliver-accent);
  opacity: 1;
}
</style>
